import * as THREE from "three";
import {
	rotateAttachWall,
	swapsFootprintAxes,
} from "../../domain/viewer/saunaRotation";
import {
	getTerrainSlopeAtPoint,
	type SiteConditions,
} from "../../domain/foundation/siteConditions";
import {
	clampTimeOfDay,
	GOLDEN_HOUR_TIME_OF_DAY,
	isNightTimeOfDay,
	NIGHT_TIME_OF_DAY,
	normalizeTimeOfDay,
	type TimeOfDayValue,
} from "../../domain/viewer/timeOfDay";
import { ENGINE_JOIST_H_M as JOIST_H } from "../../engines/constants";
import type { DeckConfig, SaunaDimensions } from "../../engines/types";

/**
 * Real HDRI assets (Polyhaven .hdr, RGBE-encoded). The procedural
 * envMap (createStudioEnvMap below) is the immediate fallback —
 * these only load on `balanced`/`high` tiers and only swap in once
 * decoded, so the viewer never blocks on the fetch.
 */
export const HDRI_DAY_URL = "/assets/hdri/outdoor-day.hdr";
export const HDRI_EVENING_URL = "/assets/hdri/outdoor-evening.hdr";
export const HDRI_DAY_GAINMAP = {
	sdr: "/assets/hdri/outdoor-day-sdr.jpg",
	gainMap: "/assets/hdri/outdoor-day-gainmap.jpg",
	metadata: "/assets/hdri/outdoor-day-gainmap.json",
} as const;
export const HDRI_EVENING_GAINMAP = {
	sdr: "/assets/hdri/outdoor-evening-sdr.jpg",
	gainMap: "/assets/hdri/outdoor-evening-gainmap.jpg",
	metadata: "/assets/hdri/outdoor-evening-gainmap.json",
} as const;

export interface HdriGainMapAsset {
	sdr: string;
	gainMap: string;
	metadata: string;
}

export interface LoadedEnvMap {
	texture: THREE.Texture;
	dispose: () => void;
}

/**
 * World Z of the front edge of the entry approach — the spot a
 * stepping-stone path or a planted bed should start from.
 *
 * Mirrors the geometry in
 * `src/engines/squareBaseBuilder/foundation.ts`:
 *   - The front facade sits at `+saunaLength / 2`.
 *   - A front-attached deck pushes it forward by `deckConfig.depth`.
 *   - The bottom stair tread sits at `frontFace + 0.82` with a
 *     0.28 m depth, so its forward edge is at `frontFace + 0.96`.
 *   - We add a small clearance so the first stone never overlaps
 *     the tread visually.
 *
 * If the entry door is on a non-front wall this still returns the
 * +Z front edge — the dressing follows the structural front, not
 * the actual door, until we wire the door wall through.
 */
const STAIR_FORWARD_OFFSET_M = 0.96;
const APPROACH_CLEARANCE_M = 0.18;

interface ApproachEdgeArgs {
	/** MODEL length (the building's own Z extent), in metres. */
	saunaLength: number;
	/** MODEL width — only consulted when the orientation swaps the axes. */
	saunaWidth?: number;
	deckConfig?: DeckConfig | null;
	/** Whole-building orientation; 0 keeps the pre-rotation behaviour exactly. */
	rotationDeg?: number;
}

/**
 * World +Z edge of the entry approach.
 *
 * Both inputs move under rotation and they move independently: a quarter turn
 * puts the building's WIDTH along world Z, while the deck's `attachWall` names
 * a model wall that may no longer face the lake at all. Rotating the wall
 * (rather than always adding the deck's depth) is what stops a side deck from
 * being ignored at 90° — where it really is the thing sticking out towards
 * the water.
 */
export function getApproachEdgeZ({
	saunaLength,
	saunaWidth,
	deckConfig,
	rotationDeg = 0,
}: ApproachEdgeArgs): number {
	const worldLength = swapsFootprintAxes(rotationDeg)
		? (saunaWidth ?? saunaLength)
		: saunaLength;
	const deckWorldWall = deckConfig
		? rotateAttachWall(deckConfig.attachWall, rotationDeg)
		: null;
	const deckFrontExtension =
		deckConfig && deckWorldWall === "front" ? deckConfig.depth : 0;
	return (
		worldLength / 2 +
		deckFrontExtension +
		STAIR_FORWARD_OFFSET_M +
		APPROACH_CLEARANCE_M
	);
}

/**
 * Global IBL strength (`scene.environmentIntensity`) through the day.
 *
 * Daylight keeps the long-standing 0.7 damping (the real HDRI over-fills
 * the cream aspen interior at 1.0). After golden hour it falls towards
 * night: the direct lights all dim across dusk, but a constant 0.7 IBL
 * kept the ground, gravel and facade lit like an overcast noon, so a
 * dusk scene read as a pale, washed-out copy of itself — and the warm
 * interior glow, the one thing that should carry a glazed sauna at
 * dusk, had nothing darker to glow against.
 */
export const DAY_ENVIRONMENT_INTENSITY = 0.7;
export const NIGHT_ENVIRONMENT_INTENSITY = 0.28;

export function getEnvironmentIntensityForTime(
	timeOfDay: TimeOfDayValue,
	interiorView = false,
): number {
	const t = clampTimeOfDay(normalizeTimeOfDay(timeOfDay));
	const k =
		Math.max(0, t - GOLDEN_HOUR_TIME_OF_DAY) /
		(NIGHT_TIME_OF_DAY - GOLDEN_HOUR_TIME_OF_DAY);
	const eased = k * k * (3 - 2 * k);
	const intensity =
		DAY_ENVIRONMENT_INTENSITY +
		(NIGHT_ENVIRONMENT_INTENSITY - DAY_ENVIRONMENT_INTENSITY) * eased;
	// IBL has no wall occlusion. Keep local lights readable in the cutaway.
	return intensity * (interiorView ? 0.55 : 1);
}

export function getHdriUrlForTime(timeOfDay: TimeOfDayValue): string {
	return isNightTimeOfDay(timeOfDay) ? HDRI_EVENING_URL : HDRI_DAY_URL;
}

export function getHdriGainMapForTime(
	timeOfDay: TimeOfDayValue,
): HdriGainMapAsset {
	return isNightTimeOfDay(timeOfDay) ? HDRI_EVENING_GAINMAP : HDRI_DAY_GAINMAP;
}

/**
 * Load a Radiance HDR equirectangular environment map.
 *
 * SSR-safe (returns null when `document` is undefined). Uses a
 * dynamic import for `HDRLoader` so the loader code only ships
 * when a tier actually needs it. Resolves to null on any failure
 * (404, decode error, network) — caller is expected to fall back
 * to the procedural envMap.
 *
 * Caller owns the returned texture and must call `.dispose()`
 * when no longer needed (per the lazy-and-disposable rule for
 * shared Three.js material libraries).
 */
export async function loadHdriEnvMap(
	url: string,
	renderer?: THREE.WebGLRenderer,
	gainMapAsset?: HdriGainMapAsset,
): Promise<LoadedEnvMap | null> {
	if (typeof document === "undefined") return null;
	if (renderer && gainMapAsset) {
		try {
			const { GainMapLoader } = await import("@monogrid/gainmap-js");
			const loader = new GainMapLoader(renderer).setRenderTargetOptions({
				mapping: THREE.EquirectangularReflectionMapping,
			});
			const result = await loader.loadAsync([
				gainMapAsset.sdr,
				gainMapAsset.gainMap,
				gainMapAsset.metadata,
			]);
			const texture = result.renderTarget.texture;
			texture.mapping = THREE.EquirectangularReflectionMapping;
			return {
				texture,
				dispose: () => {
					result.dispose(true);
				},
			};
		} catch {
			// Fall through to the legacy HDR loader below. The procedural map
			// remains the caller's final fallback if both real asset paths fail.
		}
	}
	try {
		const { HDRLoader } = await import("three/addons/loaders/HDRLoader.js");
		const loader = new HDRLoader();
		const texture = await loader.loadAsync(url);
		texture.mapping = THREE.EquirectangularReflectionMapping;
		return {
			texture,
			dispose: () => {
				texture.dispose();
			},
		};
	} catch {
		return null;
	}
}

export function getGroundBaseY(siteConditions: SiteConditions): number {
	const baselineVisibleHeight = Math.max(
		0,
		siteConditions.plinthLengthMm / 1000 -
			siteConditions.minBuriedDepthMm / 1000,
	);
	return -JOIST_H - baselineVisibleHeight;
}

/**
 * Lowest ground the sun's shadow can land on within `reachM` of the
 * origin — the plane the shadow camera's far plane has to reach
 * (issue 121).
 *
 * The receiver is never at y = 0: `getGroundBaseY` drops it by the
 * joist height plus the visible plinth, which is −0.55 m by default and
 * about −1 m on 900 mm plinths with a shallow burial. Assuming zero
 * there under-counts the projection badly at a low sun — every metre of
 * drop is another 5.7 m of shadow at golden hour's 10° elevation, far
 * past the fit's padding, and the tail gets clipped again.
 *
 * On a sloped site the ground keeps falling away from the model, so the
 * steepest descent over the whole reach is subtracted too. Guessing the
 * receiver too LOW is safe (it only widens the depth range); guessing
 * it too high re-introduces the clipping this issue is about.
 */
export function getLowestShadowReceiverY(
	siteConditions: SiteConditions,
	reachM: number,
): number {
	const centreY =
		getGroundBaseY(siteConditions) +
		getTerrainSlopeAtPoint(siteConditions, 0, 0);
	const steepestGrade =
		siteConditions.terrainMode === "slope"
			? Math.hypot(
					siteConditions.slopeAcrossPercent,
					siteConditions.slopeAlongPercent,
				) / 100
			: 0;
	return centreY - Math.abs(reachM) * steepestGrade;
}

/* ─── Ground micro-relief ─────────────────────────────────────────────
 * Adds a subtle, deterministic height jitter on top of the existing
 * terrain slope. Same hash + smoothed value-noise algorithm used in
 * the GLSL fragment patch (groundVariation.ts) so visual cues line up
 * with the actual surface micro-shape.
 *
 * The radial mask keeps the immediate footprint under the sauna
 * untouched (preserves engine alignment with the foundation) AND
 * forces the outer corners to zero — that keeps the existing
 * flat-vs-slope geometry tests stable, since their probes sit at
 * radius ≈21 m (outside the noise band).
 *
 * Amplitude is intentionally tiny (~±2 cm peak-to-peak): the goal is
 * livelier shadow micro-play in low sun, not visible bumps.
 */
const GROUND_NOISE_PERIOD_M = 3.0;
const GROUND_NOISE_AMPLITUDE_M = 0.022;
const GROUND_NOISE_INNER_FADE_FROM_M = 2.0;
const GROUND_NOISE_INNER_FADE_TO_M = 6.0;
const GROUND_NOISE_OUTER_FADE_FROM_M = 12.0;
const GROUND_NOISE_OUTER_FADE_TO_M = 17.0;

function groundHash(x: number, z: number): number {
	const s = Math.sin(x * 91.345 + z * 47.853) * 23456.789;
	return s - Math.floor(s);
}

function groundValueNoise(x: number, z: number): number {
	const xi = Math.floor(x);
	const zi = Math.floor(z);
	const xf = x - xi;
	const zf = z - zi;
	const ux = xf * xf * (3 - 2 * xf);
	const uz = zf * zf * (3 - 2 * zf);
	const a = groundHash(xi, zi);
	const b = groundHash(xi + 1, zi);
	const c = groundHash(xi, zi + 1);
	const d = groundHash(xi + 1, zi + 1);
	const top = a + (b - a) * ux;
	const bottom = c + (d - c) * ux;
	return top + (bottom - top) * uz;
}

export function getGroundReliefAt(xM: number, zM: number): number {
	const radius = Math.sqrt(xM * xM + zM * zM);
	const innerRamp = THREE.MathUtils.smoothstep(
		radius,
		GROUND_NOISE_INNER_FADE_FROM_M,
		GROUND_NOISE_INNER_FADE_TO_M,
	);
	const outerRamp =
		1 -
		THREE.MathUtils.smoothstep(
			radius,
			GROUND_NOISE_OUTER_FADE_FROM_M,
			GROUND_NOISE_OUTER_FADE_TO_M,
		);
	const mask = innerRamp * outerRamp;
	if (mask <= 0) return 0;
	const noise = groundValueNoise(
		xM / GROUND_NOISE_PERIOD_M,
		zM / GROUND_NOISE_PERIOD_M,
	);
	return (noise - 0.5) * GROUND_NOISE_AMPLITUDE_M * mask;
}

export function createGroundGeometry(
	siteConditions: SiteConditions,
): THREE.PlaneGeometry {
	const geometry = new THREE.PlaneGeometry(30, 30, 24, 24);
	const positions = geometry.attributes.position;
	const baseGroundY = getGroundBaseY(siteConditions);

	for (let index = 0; index < positions.count; index += 1) {
		const x = positions.getX(index);
		const z = -positions.getY(index);
		const slope = getTerrainSlopeAtPoint(siteConditions, x, z);
		const relief = getGroundReliefAt(x, z);
		positions.setZ(index, baseGroundY + slope + relief);
	}

	positions.needsUpdate = true;
	geometry.computeVertexNormals();
	return geometry;
}

/**
 * The lawn keep-out / grounding rectangle, in WORLD axes.
 *
 * Everything below is model-space arithmetic — `attachWall`, the L-shape arms
 * and the wrap depths are all named from the building's own frame — so the
 * rotation is applied ONCE, at the end, by exchanging the two extents. Swapping
 * only the input dimensions (and leaving `attachWall` to be read as a world
 * direction) grows the wrong axis and cuts the grass strip on the wrong side
 * of the deck.
 */
export function resolveGroundingFootprint(
	dimensions: SaunaDimensions,
	deckConfig?: DeckConfig | null,
	rotationDeg = 0,
) {
	let width = dimensions.width;
	let length = dimensions.length;

	if (deckConfig) {
		const wrapDepth = deckConfig.wrapDepth ?? deckConfig.depth;
		switch (deckConfig.shape) {
			case "rectangular":
				if (
					deckConfig.attachWall === "front" ||
					deckConfig.attachWall === "back"
				) {
					length += deckConfig.depth;
				} else {
					width += deckConfig.depth;
				}
				break;
			case "l-shape":
				width += deckConfig.armWidth ?? deckConfig.depth;
				length += deckConfig.armDepth ?? deckConfig.depth;
				break;
			case "wrap-front":
				width += wrapDepth * 2;
				length += deckConfig.depth;
				break;
			case "wrap-sides":
				width += deckConfig.depth * 2;
				length += wrapDepth;
				break;
		}
	}

	const worldWidth = swapsFootprintAxes(rotationDeg) ? length : width;
	const worldLength = swapsFootprintAxes(rotationDeg) ? width : length;

	return {
		width: worldWidth + 0.34,
		length: worldLength + 0.34,
		haloWidth: worldWidth + 1.15,
		haloLength: worldLength + 1.15,
	};
}

export interface WaterPlacement {
	/** World centre of the flat water plane. */
	center: [number, number, number];
	/** `[xExtent, zExtent]` in metres — consumed directly as planeGeometry args. */
	size: [number, number];
	/** World Z of the shore edge nearest the sauna (positive; lake is in front, +Z). */
	nearShoreZ: number;
}

/**
 * Minimum distance (m) from the sauna centre to the near shore. Matches the
 * grass disc radius (`DEFAULT_RADIUS_M` in SceneGrass) so the lawn runs
 * right down to the water's edge without blades poking through the surface.
 */
const WATER_NEAR_SHORE_M = 8;
/** Extra clearance beyond the entry approach (stairs/deck/stepping-stones). */
const WATER_APPROACH_MARGIN_M = 3;
/** Extend beyond the visible horizon; SceneSky fog hides the distant edges. */
const WATER_HORIZON_EXTENT_M = 90;
/**
 * Surface sits just ABOVE the ground plane. The ground is a single opaque
 * sheet with no cut-out, so a recessed (below-ground) surface would be
 * fully hidden by it. A small positive lift lets the water render on top
 * of the lawn — same trick the grounding-overlay planes use.
 */
const WATER_SURFACE_LIFT_M = 0.03;

/**
 * Place the lakeside water plane in FRONT of the sauna (the "bastu vid
 * vattnet" composition). The lake sits on the +Z side — the side the entry,
 * deck, and stepping-stones face — so the sauna looks out over the water.
 * The near shore clears the entry approach (stairs/deck/stones) and the
 * grass disc, so the lawn runs down to the water's edge. Pure +
 * deterministic so the placement is unit-testable without a renderer.
 */
export function resolveWaterPlacement(
	dimensions: SaunaDimensions,
	deckConfig: DeckConfig | null | undefined,
	groundY: number,
	rotationDeg = 0,
): WaterPlacement {
	// The entry approach (front face → bottom stair tread, plus any
	// front-attached deck) ends at this +Z edge; the shore starts beyond it.
	const approachEdgeZ = getApproachEdgeZ({
		saunaLength: dimensions.length,
		saunaWidth: dimensions.width,
		deckConfig,
		rotationDeg,
	});
	const nearShoreZ = Math.max(
		WATER_NEAR_SHORE_M,
		approachEdgeZ + WATER_APPROACH_MARGIN_M,
	);
	const depthZ = Math.max(2, WATER_HORIZON_EXTENT_M - nearShoreZ);
	const centerZ = nearShoreZ + depthZ / 2;
	return {
		center: [0, groundY + WATER_SURFACE_LIFT_M, centerZ],
		size: [WATER_HORIZON_EXTENT_M * 2, depthZ],
		nearShoreZ,
	};
}

export function createBackdropTexture({
	top,
	horizon,
	bottom,
	accent,
	vignetteStrength,
	highlightStrength,
}: {
	top: number;
	horizon: number;
	bottom: number;
	accent: number;
	vignetteStrength: number;
	highlightStrength: number;
}): THREE.DataTexture {
	const width = 64;
	const height = 128;
	const data = new Uint8Array(width * height * 4);
	const topColor = new THREE.Color(top);
	const horizonColor = new THREE.Color(horizon);
	const bottomColor = new THREE.Color(bottom);
	const accentColor = new THREE.Color(accent);

	for (let y = 0; y < height; y += 1) {
		for (let x = 0; x < width; x += 1) {
			const tx = x / Math.max(1, width - 1);
			const ty = y / Math.max(1, height - 1);
			const horizonMix = Math.max(0, 1 - Math.abs(ty - 0.58) / 0.34);
			const glowMix = Math.max(
				0,
				1 - Math.sqrt((tx - 0.42) ** 2 + (ty - 0.36) ** 2) / 0.48,
			);
			const vignetteMix =
				1 - Math.min(1, Math.sqrt((tx - 0.5) ** 2 + (ty - 0.5) ** 2) / 0.72);
			const verticalBase = topColor.clone().lerp(bottomColor, ty);
			const color = verticalBase
				.lerp(horizonColor, horizonMix * 0.45)
				.lerp(accentColor, glowMix * highlightStrength)
				.multiplyScalar(1 - vignetteStrength + vignetteMix * vignetteStrength);
			const index = (y * width + x) * 4;
			data[index] = Math.round(color.r * 255);
			data[index + 1] = Math.round(color.g * 255);
			data[index + 2] = Math.round(color.b * 255);
			data[index + 3] = 255;
		}
	}

	const texture = new THREE.DataTexture(data, width, height, THREE.RGBAFormat);
	texture.needsUpdate = true;
	texture.mapping = THREE.EquirectangularReflectionMapping;
	texture.colorSpace = THREE.SRGBColorSpace;
	return texture;
}

export function createStudioEnvMap({
	isEvening,
	reflectionBoost,
}: {
	isEvening: boolean;
	reflectionBoost: number;
}): THREE.DataTexture {
	const width = 128;
	const height = 64;
	const data = new Uint8Array(width * height * 4);

	const skyTop = new THREE.Color(isEvening ? 0x141c32 : 0xd2ddec);
	const skyFill = new THREE.Color(isEvening ? 0x304060 : 0xaebfce);
	const horizonWarm = new THREE.Color(isEvening ? 0x8f5333 : 0xe8c9a6);
	const ground = new THREE.Color(isEvening ? 0x161812 : 0x6f7767);
	const keyLobe = new THREE.Color(isEvening ? 0xffb56d : 0xf8f4eb);
	const coolBounce = new THREE.Color(isEvening ? 0x4d5f8d : 0x99b3cf);
	const clamp = THREE.MathUtils.clamp;
	const brightness = clamp(reflectionBoost, 0.85, 1.3);

	for (let y = 0; y < height; y += 1) {
		const ty = y / Math.max(1, height - 1);
		for (let x = 0; x < width; x += 1) {
			const tx = x / Math.max(1, width - 1);
			const skyMix = Math.max(0, (ty - 0.42) / 0.58);
			const horizonMix = Math.max(0, 1 - Math.abs(ty - 0.48) / 0.18);
			const keyMask = Math.max(
				0,
				1 - Math.sqrt((tx - 0.26) ** 2 + (ty - 0.31) ** 2) / 0.22,
			);
			const coolMask = Math.max(
				0,
				1 - Math.sqrt((tx - 0.78) ** 2 + (ty - 0.28) ** 2) / 0.34,
			);

			const base = ground.clone().lerp(skyFill, Math.min(1, ty / 0.56));
			const color = base
				.lerp(skyTop, skyMix * 0.82)
				.lerp(horizonWarm, horizonMix * 0.55)
				.lerp(coolBounce, coolMask * 0.26)
				.lerp(keyLobe, keyMask * 0.92)
				.multiplyScalar(brightness);

			const idx = (y * width + x) * 4;
			data[idx] = Math.round(clamp(color.r, 0, 1) * 255);
			data[idx + 1] = Math.round(clamp(color.g, 0, 1) * 255);
			data[idx + 2] = Math.round(clamp(color.b, 0, 1) * 255);
			data[idx + 3] = 255;
		}
	}

	const texture = new THREE.DataTexture(data, width, height, THREE.RGBAFormat);
	texture.mapping = THREE.EquirectangularReflectionMapping;
	texture.colorSpace = THREE.SRGBColorSpace;
	texture.needsUpdate = true;
	return texture;
}

export function createGroundOverlayTexture({
	coreColor,
	edgeColor,
	falloff,
}: {
	coreColor: number;
	edgeColor: number;
	falloff: number;
}): THREE.DataTexture {
	const size = 128;
	const data = new Uint8Array(size * size * 4);
	const core = new THREE.Color(coreColor);
	const edge = new THREE.Color(edgeColor);

	for (let y = 0; y < size; y += 1) {
		for (let x = 0; x < size; x += 1) {
			const tx = x / Math.max(1, size - 1);
			const ty = y / Math.max(1, size - 1);
			const dx = (tx - 0.5) / 0.5;
			const dy = (ty - 0.5) / 0.5;
			const radial = Math.min(1, Math.sqrt(dx * dx + dy * dy));
			const softness = 1 - radial ** falloff;
			const alpha = THREE.MathUtils.clamp(softness, 0, 1);
			const color = edge.clone().lerp(core, alpha);
			const index = (y * size + x) * 4;
			data[index] = Math.round(color.r * 255);
			data[index + 1] = Math.round(color.g * 255);
			data[index + 2] = Math.round(color.b * 255);
			data[index + 3] = Math.round(alpha * 255);
		}
	}

	const texture = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
	texture.colorSpace = THREE.SRGBColorSpace;
	texture.needsUpdate = true;
	return texture;
}

/**
 * Procedural alpha-mask for far-field foliage tussocks. Reads as a
 * soft, asymmetric clump silhouette — the kind of thing the eye
 * registers as "distant brush" without committing to a specific
 * plant species. Used as a sprite map at ~14 m radius behind the
 * sauna, on `high` tier only.
 *
 * Generated procedurally so it costs nothing in asset budget.
 */
export function createLeafTussockTexture(): THREE.DataTexture {
	const size = 64;
	const data = new Uint8Array(size * size * 4);

	// Three offset cluster centers stitched together so the
	// silhouette has lobed asymmetry rather than reading as a circle.
	const lobes: Array<{ cx: number; cy: number; r: number }> = [
		{ cx: 0.42, cy: 0.55, r: 0.32 },
		{ cx: 0.62, cy: 0.5, r: 0.26 },
		{ cx: 0.5, cy: 0.36, r: 0.24 },
	];

	for (let y = 0; y < size; y += 1) {
		for (let x = 0; x < size; x += 1) {
			const tx = x / Math.max(1, size - 1);
			const ty = y / Math.max(1, size - 1);
			let alpha = 0;
			for (const lobe of lobes) {
				const dx = (tx - lobe.cx) / lobe.r;
				const dy = (ty - lobe.cy) / lobe.r;
				const d = Math.sqrt(dx * dx + dy * dy);
				const lobeAlpha = THREE.MathUtils.clamp(1 - d, 0, 1);
				alpha = Math.max(alpha, lobeAlpha);
			}
			// Soft falloff so the silhouette reads as foliage, not a
			// hard sticker. Squared smoothstep gives a natural drop.
			alpha = alpha * alpha * (3 - 2 * alpha);
			const index = (y * size + x) * 4;
			data[index] = 0;
			data[index + 1] = 0;
			data[index + 2] = 0;
			data[index + 3] = Math.round(alpha * 255);
		}
	}

	const texture = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
	texture.colorSpace = THREE.SRGBColorSpace;
	texture.needsUpdate = true;
	return texture;
}
